import React from 'react';
import { useQueries, useQuery, useInfiniteQuery, type QueryClient } from '@tanstack/react-query';

import {
  fetchSchedules,
  fetchScheduleUpcoming,
  fetchScheduleWeeks,
  fetchScheduleWeekEvents,
  fetchFootball3dWeeksAndEvents,
  fetchScheduleStandings,
  fetchScheduleEventResults,
  fetchScheduleResults,
  fetchScheduleResultsPage,
  UPCOMING_EVENTS_LIST_SIZE,
  type ScheduleStanding,
  type ScheduleResultItem,
  type ScheduleUpcomingResponse,
  type ScheduleUpcomingFootball3d,
  type ScheduleEventResultsResponse,
} from './api';
import type { Football3dWeek } from './api';
import type { Event, Schedule } from './types';

/** Retry policy for REST-first endpoints: 10 retries, 1 second apart, no duplicate in-flight (React Query dedupes by key) */
const REST_RETRY = 10;
const REST_RETRY_DELAY_MS = 1000;

function getHttpStatus(err: unknown): number | null {
  const e: any = err;
  return e?.response?.status ?? e?.request?.status ?? null;
}

export const eventsQueryKeys = {
  schedules: ['events', 'schedules'] as const,
  /** Single key for schedule upcoming (raw response); shared so sport switch uses cache. */
  scheduleUpcoming: (scheduleId: number | string, numEvents: number = UPCOMING_EVENTS_LIST_SIZE) =>
    ['events', 'scheduleUpcoming', String(scheduleId), numEvents] as const,
  /** GET /web/schedules/:id/events/:eventId/results */
  scheduleEventResults: (scheduleId: number | string, eventId: number | string) =>
    ['events', 'scheduleEventResults', scheduleId, eventId] as const,
  scheduleWeeks: (scheduleId: number | string) =>
    ['events', 'scheduleWeeks', scheduleId] as const,
  scheduleWeekEvents: (scheduleId: number | string, weekId: number) =>
    ['events', 'scheduleWeekEvents', scheduleId, weekId] as const,
  scheduleStandings: (scheduleId: number | string, weekId?: number) =>
    ['events', 'scheduleStandings', scheduleId, weekId] as const,
  scheduleResults: (scheduleId: number | string) =>
    ['events', 'scheduleResults', scheduleId] as const,
  /** Football3D: full schedule results (no pagination), distinct from paginated scheduleResults */
  football3dScheduleResults: (scheduleId: number | string) =>
    ['events', 'football3dScheduleResults', scheduleId] as const,
};

export function useSchedulesQuery() {
  return useQuery({
    queryKey: eventsQueryKeys.schedules,
    queryFn: fetchSchedules,
    // Schedules are effectively static during a session; avoid refetch storms
    // when many components mount (e.g. many GridOdd buttons).
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

function flattenScheduleUpcomingToEvents(data: ScheduleUpcomingResponse | undefined): Event[] {
  if (!data || typeof data !== 'object') return [];
  if ('events' in data && Array.isArray(data.events)) return data.events as Event[];
  if ('weeks' in data && Array.isArray(data.weeks)) {
    const list = (data.weeks as Football3dWeek[]).flatMap((w) => w.events ?? []);
    return [...list]
      .filter((e: any) => e?.event_timestamp != null)
      .sort((a: any, b: any) => Number(a.event_timestamp) - Number(b.event_timestamp));
  }
  return [];
}

/** Exported for consumers that read raw cache (e.g. ScheduleSelectorBar) */
export { flattenScheduleUpcomingToEvents };

export function useUpcomingEventsQuery(params: {
  scheduleId: number | string | null | undefined;
  numEvents: number;
}) {
  const { scheduleId, numEvents } = params;

  return useQuery({
    queryKey:
      scheduleId != null
        ? eventsQueryKeys.scheduleUpcoming(scheduleId, numEvents)
        : (['events', 'scheduleUpcoming', 'none', numEvents] as const),
    queryFn: async () => {
      if (scheduleId == null) return { events: [], ongoing: null } as ScheduleUpcomingResponse;
      return fetchScheduleUpcoming({ schedule_id: scheduleId, num_events: numEvents });
    },
    enabled: scheduleId != null,
    select: (data) => flattenScheduleUpcomingToEvents(data) as Event[],
    staleTime: 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

export function useAllUpcomingEventsQueries(params: {
  schedules: Schedule[];
  numEvents: number;
}) {
  const { schedules, numEvents } = params;
  const scheduleIds = schedules.map((s) => s.schedule_id);
  // Stable order so reordering schedules does not change the queries array and trigger refetch storm.
  const stableScheduleIds = React.useMemo(() => {
    const sorted = [...scheduleIds].map(String).sort();
    return sorted;
  }, [scheduleIds.map((id) => String(id)).sort().join(',')]);

  return useQueries({
    queries: stableScheduleIds.map((scheduleId) => ({
      queryKey: eventsQueryKeys.scheduleUpcoming(scheduleId, numEvents),
      queryFn: async () => {
        const res = await fetchScheduleUpcoming({ schedule_id: scheduleId, num_events: numEvents });
        return res;
      },
      enabled: scheduleId != null,
      staleTime: 60 * 60 * 1000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    })),
  });
}

export function useScheduleWeeksQuery(params: { scheduleId: number | string | null | undefined }) {
  const { scheduleId } = params;
  return useQuery({
    queryKey: scheduleId != null ? eventsQueryKeys.scheduleWeeks(scheduleId) : (['events', 'scheduleWeeks', 'none'] as const),
    queryFn: () => {
      if (scheduleId == null) return Promise.resolve([] as number[]);
      return fetchScheduleWeeks({ schedule_id: scheduleId });
    },
    enabled: scheduleId != null,
  });
}

export function useScheduleWeekEventsQuery(params: { scheduleId: number | string | null | undefined; weekId: number | null | undefined }) {
  const { scheduleId, weekId } = params;
  return useQuery({
    queryKey:
      scheduleId != null && weekId != null
        ? eventsQueryKeys.scheduleWeekEvents(scheduleId, weekId)
        : (['events', 'scheduleWeekEvents', 'none'] as const),
    queryFn: () => {
      if (scheduleId == null || weekId == null) return Promise.resolve([] as Event[]);
      return fetchScheduleWeekEvents({ schedule_id: scheduleId, week_id: weekId });
    },
    enabled: scheduleId != null && weekId != null,
  });
}

export function useScheduleWeekEventsQueries(params: {
  scheduleId: number | string | null | undefined;
  weekIds: number[];
}) {
  const { scheduleId, weekIds } = params;
  const safeWeekIds = Array.isArray(weekIds) ? weekIds : [];
  return useQueries({
    queries: safeWeekIds.map((weekId) => ({
      queryKey: scheduleId != null ? eventsQueryKeys.scheduleWeekEvents(scheduleId, weekId) : (['events', 'scheduleWeekEvents', 'none'] as const),
      queryFn: () => {
        if (scheduleId == null) return Promise.resolve([] as Event[]);
        return fetchScheduleWeekEvents({ schedule_id: scheduleId, week_id: weekId });
      },
      enabled: scheduleId != null && weekId != null,
      staleTime: 15_000,
    })),
  });
}

export function useScheduleStandingsQuery(params: {
  scheduleId: number | string | null | undefined;
  week_id?: number | null;
}) {
  const { scheduleId, week_id } = params;
  return useQuery<ScheduleStanding[]>({
    queryKey: scheduleId != null ? eventsQueryKeys.scheduleStandings(scheduleId, week_id ?? undefined) : (['events', 'scheduleStandings', 'none'] as const),
    queryFn: () => {
      if (scheduleId == null) return Promise.resolve([]);
      return fetchScheduleStandings({ schedule_id: scheduleId, week_id: week_id ?? undefined });
    },
    enabled: scheduleId != null,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useScheduleResultsQuery(params: {
  scheduleId: number | string | null | undefined;
}) {
  const { scheduleId } = params;
  const PAGE_SIZE = 10;

  const infinite = useInfiniteQuery({
    queryKey: scheduleId != null ? eventsQueryKeys.scheduleResults(String(scheduleId)) : (['events', 'scheduleResults', 'none'] as const),
    queryFn: ({ pageParam }) =>
      fetchScheduleResultsPage({
        schedule_id: scheduleId!,
        limit: PAGE_SIZE,
        offset: pageParam as number,
      }),
    initialPageParam: 0 as number,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.has_more ? allPages.length * PAGE_SIZE : undefined,
    enabled: scheduleId != null,
    staleTime: 30_000,
  });

  const data: ScheduleResultItem[] =
    infinite.data?.pages?.flatMap((p) => p.results ?? []) ?? [];
  const isLoading = infinite.isLoading && infinite.data?.pages == null;
  const isFetching = infinite.isFetching;
  const isError = infinite.isError;
  const error = infinite.error;

  return {
    data,
    isLoading,
    isFetching,
    isError,
    error: isError ? (error ?? new Error('Failed to load results')) : null,
    isRefetching: isFetching && !infinite.isFetchingNextPage,
    refetch: () => infinite.refetch(),
    fetchNextPage: infinite.fetchNextPage,
    hasNextPage: infinite.hasNextPage,
    isFetchingNextPage: infinite.isFetchingNextPage,
  };
}

/**
 * Football3D: one call to get all schedule results (no pagination).
 * Use this for markets area and mini TV sidebar so we don't issue per-event results calls.
 */
export function useFootball3dScheduleResultsQuery(params: {
  scheduleId: number | string | null | undefined;
}) {
  const { scheduleId } = params;
  return useQuery<ScheduleResultItem[]>({
    queryKey:
      scheduleId != null
        ? eventsQueryKeys.football3dScheduleResults(scheduleId)
        : (['events', 'football3dScheduleResults', 'none'] as const),
    queryFn: () => {
      if (scheduleId == null) return Promise.resolve([]);
      return fetchScheduleResults({ schedule_id: scheduleId });
    },
    enabled: scheduleId != null,
    staleTime: 30_000,
  });
}

export function useFootball3dWeeksAndEventsQuery(params: {
  scheduleId: number | string | null | undefined;
}) {
  const { scheduleId } = params;
  const query = useScheduleUpcomingQuery({
    scheduleId: scheduleId ?? undefined,
    numEvents: UPCOMING_EVENTS_LIST_SIZE,
  });

  const data = React.useMemo(() => {
    const raw = query.data;
    if (!raw || typeof raw !== 'object') return { weeks: [] as Football3dWeek[] };
    if ('weeks' in raw && Array.isArray(raw.weeks)) {
      return { weeks: raw.weeks as Football3dWeek[] };
    }
    return { weeks: [] as Football3dWeek[] };
  }, [query.data]);

  return {
    ...query,
    data,
  };
}

/**
 * REST-first: GET /web/schedules/:scheduleId/events/upcoming (events or weeks+eventsByWeek only; ongoing from GET /live).
 * Shares cache with useUpcomingEventsQuery and useAllUpcomingEventsQueries so switching sports does not refetch.
 */
export function useScheduleUpcomingQuery(params: {
  scheduleId: number | string | null | undefined;
  numEvents?: number;
}) {
  const { scheduleId, numEvents = UPCOMING_EVENTS_LIST_SIZE } = params;
  return useQuery({
    queryKey: scheduleId != null ? eventsQueryKeys.scheduleUpcoming(scheduleId, numEvents) : (['events', 'scheduleUpcoming', 'none', numEvents] as const),
    queryFn: () => {
      if (scheduleId == null) return Promise.resolve({ events: [], ongoing: null } as ScheduleUpcomingResponse);
      return fetchScheduleUpcoming({ schedule_id: scheduleId, num_events: numEvents });
    },
    enabled: scheduleId != null,
    retry: REST_RETRY,
    retryDelay: REST_RETRY_DELAY_MS,
    staleTime: 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * GET /web/schedules/:scheduleId/events/:eventId/results (event results with VOD for mini TV).
 */
export type ScheduleEventResultsQueryRefetchContext = {
  state: {
    data?: ScheduleEventResultsResponse | undefined;
    error: unknown;
    failureCount: number;
  };
};

export function useScheduleEventResultsQuery(params: {
  scheduleId: number | string | null | undefined;
  eventId: number | string | null | undefined;
  enabled?: boolean;
  /** Poll until data has video (e.g. when LIVE for mini TV). Pass ms or function. Function receives query context so it can stop on error (e.g. 404). */
  refetchInterval?: number | false | ((ctx: ScheduleEventResultsQueryRefetchContext) => number | false);
}) {
  const { scheduleId, eventId, enabled = true, refetchInterval } = params;
  return useQuery({
    queryKey:
      scheduleId != null && eventId != null
        ? eventsQueryKeys.scheduleEventResults(scheduleId, eventId)
        : (['events', 'scheduleEventResults', 'none'] as const),
    queryFn: () => {
      if (scheduleId == null || eventId == null) return Promise.resolve({} as ScheduleEventResultsResponse);
      return fetchScheduleEventResults({ schedule_id: scheduleId, event_id: eventId });
    },
    enabled: enabled && scheduleId != null && eventId != null,
    retry: (failureCount, error) => {
      const status = getHttpStatus(error);
      if (status === 404 || status === 410) return false;
      return failureCount < REST_RETRY;
    },
    retryDelay: (_attemptIndex, error) => {
      const status = getHttpStatus(error);
      if (status === 404 || status === 410) return 0;
      return REST_RETRY_DELAY_MS;
    },
    staleTime: 60_000,
    refetchInterval:
      typeof refetchInterval === 'function'
        ? (query) =>
            refetchInterval({
              state: {
                data: query.state.data as ScheduleEventResultsResponse | undefined,
                error: query.state.error,
                failureCount: query.state.failureCount,
              },
            })
        : refetchInterval ?? false,
  });
}

/**
 * Fetch fresh upcoming events and MERGE with existing cache so
 * events/weeks already visible in the selector stay in place.
 */
export async function fetchAndMergeUpcoming(
  queryClient: QueryClient,
  scheduleId: string | number,
  isFootball3d: boolean,
): Promise<void> {
  const key = eventsQueryKeys.scheduleUpcoming(scheduleId);
  const prev = queryClient.getQueryData<ScheduleUpcomingResponse>(key);

  let fresh: ScheduleUpcomingResponse;
  try {
    fresh = await fetchScheduleUpcoming({
      schedule_id: scheduleId,
      num_events: UPCOMING_EVENTS_LIST_SIZE,
    });
  } catch {
    return;
  }

  if (isFootball3d && prev && 'weeks' in prev && fresh && 'weeks' in fresh) {
    const prevWeeks = (prev as ScheduleUpcomingFootball3d).weeks;
    const freshWeeks = (fresh as ScheduleUpcomingFootball3d).weeks;
    const weekMap = new Map<number, Football3dWeek>();
    for (const w of prevWeeks) weekMap.set(w.week_id, w);
    for (const w of freshWeeks) {
      const existing = weekMap.get(w.week_id);
      if (!existing) {
        weekMap.set(w.week_id, w);
      } else {
        const existingIds = new Set((existing.events ?? []).map((e: any) => String(e.event_id)));
        const newEvents = (w.events ?? []).filter((e: any) => !existingIds.has(String(e.event_id)));
        if (newEvents.length > 0) {
          weekMap.set(w.week_id, {
            ...existing,
            ...w,
            events: [...(existing.events ?? []), ...newEvents].sort(
              (a: any, b: any) => Number(a.event_timestamp) - Number(b.event_timestamp)
            ),
          });
        } else {
          weekMap.set(w.week_id, { ...existing, ...w });
        }
      }
    }
    queryClient.setQueryData<ScheduleUpcomingResponse>(key, {
      ...fresh,
      weeks: Array.from(weekMap.values()),
    });
  } else if (!isFootball3d && prev && 'events' in prev && fresh && 'events' in fresh) {
    const existingIds = new Set(prev.events.map((e: any) => String(e.event_id)));
    const newEvents = fresh.events.filter((e: any) => !existingIds.has(String(e.event_id)));
    const merged = [...prev.events, ...newEvents].sort(
      (a: any, b: any) => Number(a.event_timestamp) - Number(b.event_timestamp)
    );
    queryClient.setQueryData<ScheduleUpcomingResponse>(key, { ...fresh, events: merged });
  } else {
    queryClient.setQueryData<ScheduleUpcomingResponse>(key, fresh);
  }
}
